'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getCookieValue, IS_SSR } from '@libs/common-next';
import { stringifyStoreId } from '@libs/core';
import { STORE_COOKIE_NAME } from './constants';
import { StoreContext, StoreDispatchContext } from './context';
import { useStoreQuery } from './queries';
import type { StoreProviderDispatch, StoreProviderState } from './types';
import { removeStoreCookie, setStoreCookie } from './utils';

/**
 * In the previous implementation there was no difference between selected and favorite stores
 * That's why we are not supporting favorite store any more.
 *
 * In the future, we will extend this provider with favorite and any other types of Stores that are added
 */
export function StoreProvider(t0) {
  const $ = _c(17);
  const {
    children
  } = t0;
  const [selectedStoreId, setSelectedStoreId] = useState(_temp);
  const selectedStore = useStoreQuery(selectedStoreId);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = storeId => {
      const parsedStoreId = typeof storeId === "number" ? stringifyStoreId(storeId) as string : storeId;
      setStoreCookie(parsedStoreId);
      setSelectedStoreId(parsedStoreId);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const updateSelectedStore = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      removeStoreCookie();
      setSelectedStoreId(undefined);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const clearSelectedStore = t2;
  const isPermanentlyClosed = selectedStore.data?.IsActive === false;
  let t3;
  let t4;
  if ($[2] !== isPermanentlyClosed) {
    t3 = () => {
      if (isPermanentlyClosed) {
        clearSelectedStore();
      }
    };
    t4 = [clearSelectedStore, isPermanentlyClosed];
    $[2] = isPermanentlyClosed;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] !== isPermanentlyClosed || $[6] !== selectedStore) {
    t5 = isPermanentlyClosed ? {
      ...selectedStore,
      data: null
    } as StoreProviderState['selectedStore'] : selectedStore;
    $[5] = isPermanentlyClosed;
    $[6] = selectedStore;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = isPermanentlyClosed ? undefined : selectedStoreId;
  let t7;
  if ($[8] !== t5 || $[9] !== t6) {
    t7 = {
      selectedStore: t5,
      selectedStoreId: t6
    };
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const stateValue = t7;
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      updateSelectedStore,
      clearSelectedStore
    };
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const dispatchValue = t8;
  let t9;
  if ($[12] !== children) {
    t9 = <StoreDispatchContext.Provider value={dispatchValue}>{children}</StoreDispatchContext.Provider>;
    $[12] = children;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== stateValue || $[15] !== t9) {
    t10 = <StoreContext.Provider value={stateValue}>{t9}</StoreContext.Provider>;
    $[14] = stateValue;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  return t10;
}
function _temp() {
  return IS_SSR ? undefined : getCookieValue(STORE_COOKIE_NAME);
}